import { FC } from 'react'
import { useTranslation } from 'react-i18next'
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined'
import { Stack } from '@mui/material'
import _ from 'lodash'
import { DateTime, Info } from 'luxon'

import { formatDate } from '@/common/utils/locale'
import { fromHHMM, toHHMM } from '@/common/utils/time'
import { DatePicker } from '@/components/componentLibrary'
import { DatePickerDropdownValue } from '@/components/componentLibrary/DatePicker/types'
import { allDropDownOptions } from '@/components/componentLibrary/DatePicker/utils'
import { WeekdaysToggle } from '@/components/curb/WeekdaysToggle'

import { PolicyTimeFrameType } from '../../../utils/types'
import { EditableFieldProps } from '../../PolicyDetails/Form/fields/types'

import { ThemedTimeField } from './inputs'
import { ThemedField, ValueChips, ValueText } from './ThemedField'

// The fields below show and hide in the same cases as the old fields in
// `PolicyDetails/Form/fields`.

/** Policies have no "this month" or "last month" dates, the same as the old field. */
const POLICY_DATE_OPTIONS = allDropDownOptions.filter(
  (option: DatePickerDropdownValue) => option !== 'thisMonth' && option !== 'lastMonth'
)

const formatPolicyDate = (date: DateTime) => formatDate(date.toJSDate(), { dateStyle: 'medium' })

export const DateRangeField: FC<EditableFieldProps> = ({
  editable,
  editPolicy,
  onValidate,
  policy,
  ...props
}) => {
  const { t } = useTranslation()
  const label = t('policiesLibrary.formDateRangeTitle', 'Effective Dates')
  const { startDate, endDate } = policy

  if (!editable)
    return (
      <ThemedField label={label}>
        {startDate || endDate ? (
          <Stack direction="row" alignItems="center" spacing={1}>
            <CalendarTodayOutlinedIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
            <ValueText>
              {startDate
                ? formatPolicyDate(startDate)
                : t('policiesLibrary.formNoStart', 'No Start Date')}
              {' – '}
              {endDate ? formatPolicyDate(endDate) : t('policiesLibrary.formNoEnd', 'No End Date')}
            </ValueText>
          </Stack>
        ) : (
          <ValueText>{t('policiesLibrary.formNoneSelected', 'None Selected')}</ValueText>
        )}
      </ThemedField>
    )

  return (
    <ThemedField label={label} {...props}>
      <DatePicker
        range={{ from: startDate?.toJSDate() || null, to: endDate?.toJSDate() || null }}
        setRange={range => {
          editPolicy?.(p => {
            p.setStartDate(range.from ? DateTime.fromJSDate(range.from) : undefined)
            p.setEndDate(range.to ? DateTime.fromJSDate(range.to) : undefined)
          })
          // Check for errors whenever the dates change.
          onValidate?.()
        }}
        dropdownOptions={POLICY_DATE_OPTIONS}
      />
    </ThemedField>
  )
}

export const DaysField: FC<EditableFieldProps> = ({
  editable,
  editPolicy,
  onValidate,
  policy,
  ...props
}) => {
  const { t, i18n } = useTranslation()
  const label = t('policiesLibrary.formDaysOfWeekTitle', 'Days Of The Week')
  const variableFee = _.includes(policy.parkingFeeType, 'variable')

  if (!editable) {
    // Chips, not the disabled toggle: a disabled toggle does not show which days are selected.
    const weekdayNames = Info.weekdays('short', { locale: i18n.language })
    return (
      <ThemedField label={label} hidden={_.isEmpty(policy.days) || variableFee}>
        <ValueChips values={(policy.days ?? []).map(day => weekdayNames[day - 1])} />
      </ThemedField>
    )
  }

  return (
    <ThemedField
      label={label}
      info={t(
        'policiesLibrary.formDaysTooltip',
        'Policy applies for each day of the week selected.'
      )}
      {...props}
      hidden={variableFee}
    >
      <WeekdaysToggle
        showLabel={false}
        selectedDays={policy.days || []}
        onChange={days => {
          editPolicy?.(p => p.setDays(days))
          onValidate?.()
        }}
      />
    </ThemedField>
  )
}

export const TimeFrameField: FC<EditableFieldProps> = ({
  editable,
  editPolicy,
  onValidate,
  policy,
  ...props
}) => {
  const { t } = useTranslation()
  const label = t('policiesLibrary.formTimeFrameTitle', 'Daily Time Frame')
  const hourOnly = policy.policyType.timeframeType === PolicyTimeFrameType.HOURS
  const startTime = fromHHMM(policy.startTime)
  const endTime = fromHHMM(policy.endTime)

  if (!editable)
    return (
      <ThemedField
        label={label}
        hidden={!(policy.startTime || policy.endTime)}
        info={
          policy.isOvernight &&
          t('policiesLibrary.formOvernightPolicy', 'This is an overnight policy.')
        }
      >
        <ValueText>
          {toHHMM(startTime ?? undefined) || '--:--'}
          {' – '}
          {toHHMM(endTime ?? undefined) || '--:--'}
        </ValueText>
      </ThemedField>
    )

  return (
    <ThemedField
      label={label}
      info={
        policy.optionalOvernight &&
        t(
          'policiesLibrary.formOvernightTooltip',
          'Create an overnight policy by selecting start time later than end time (e.g. 21:00 - 06:00). Day of week selections apply to the day on which the policy starts.'
        )
      }
      {...props}
    >
      <Stack direction="row" useFlexGap sx={{ flexWrap: 'wrap', gap: 2, pt: 0.75 }}>
        <ThemedTimeField
          label={
            hourOnly
              ? t('policiesLibrary.formStartHour', 'Start Hour (24hr)')
              : t('policiesLibrary.formStartTime', 'Start Time (24hr)')
          }
          hourOnly={hourOnly}
          error={props.error}
          value={startTime}
          onChange={time => editPolicy?.(p => p.setStartTime(time && toHHMM(time)))}
          onBlur={onValidate}
        />
        <ThemedTimeField
          label={
            hourOnly
              ? t('policiesLibrary.formEndHour', 'End Hour (24hr)')
              : t('policiesLibrary.formEndTime', 'End Time (24hr)')
          }
          hourOnly={hourOnly}
          error={props.error}
          value={endTime}
          onChange={time => editPolicy?.(p => p.setEndTime(time && toHHMM(time)))}
          onBlur={onValidate}
        />
      </Stack>
    </ThemedField>
  )
}
